import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

export type CategoriaGasto = Database['public']['Tables']['categorias_gasto']['Row']
export type CategoriaGastoInsert = Database['public']['Tables']['categorias_gasto']['Insert']
export type CategoriaGastoUpdate = Database['public']['Tables']['categorias_gasto']['Update']

/**
 * Servicio para el catálogo de categorías de gasto del estudio.
 *
 * Aislamiento multi-tenant a nivel app: TODA query filtra por `organizacion_id`
 * (la RLS solo verifica `role = 'authenticated'`, ver .claude/rules/database.md).
 */
export const categoriasGastoService = {
  async listar(
    client: SupabaseClient<Database>,
    organizacionId: string,
    soloActivas = true,
  ): Promise<CategoriaGasto[]> {
    let query = client
      .from('categorias_gasto')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .order('nombre')

    if (soloActivas) {
      query = query.eq('activa', true)
    }

    const { data, error } = await query
    if (error) throw error
    return data as CategoriaGasto[]
  },

  async crear(
    client: SupabaseClient<Database>,
    categoria: CategoriaGastoInsert,
  ): Promise<CategoriaGasto> {
    const { data, error } = await client
      .from('categorias_gasto')
      .insert(categoria)
      .select()
      .single()

    if (error) throw error
    return data as CategoriaGasto
  },

  async actualizar(
    client: SupabaseClient<Database>,
    organizacionId: string,
    id: string,
    categoria: CategoriaGastoUpdate,
  ): Promise<CategoriaGasto> {
    const { data, error } = await client
      .from('categorias_gasto')
      .update(categoria)
      .eq('id', id)
      // Aislamiento multi-tenant app-layer: NUNCA actualizar categorías de otra org.
      .eq('organizacion_id', organizacionId)
      .select()
      .single()

    if (error) throw error
    return data as CategoriaGasto
  },

  /** Soft-delete: marca la categoría como inactiva (no se borra físicamente). */
  async desactivar(
    client: SupabaseClient<Database>,
    organizacionId: string,
    id: string,
  ): Promise<void> {
    const { error } = await client
      .from('categorias_gasto')
      .update({ activa: false })
      .eq('id', id)
      // Aislamiento multi-tenant app-layer: NUNCA desactivar categorías de otra org.
      .eq('organizacion_id', organizacionId)

    if (error) throw error
  },
}
